/**
 * Conversations tab (§4.4.6).
 *
 * v1 shows the session list only — clicking a row opens a placeholder
 * ("Transcript viewer coming soon"). Full transcript rendering lives
 * in the top-level /activity page, not here.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AgentConfig } from '@/api/agents';
import { api } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { formatBytes, formatRelative } from '@/lib/format';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ConversationSession {
  session_id: string;
  first_message_preview?: string;
  last_activity_at?: string;
  message_count: number;
  size_bytes: number;
  agent: string;
}

interface ConversationListResponse {
  items: ConversationSession[];
  next_cursor?: string;
}

interface ConversationsProps {
  agent: AgentConfig;
}

export default function Conversations({ agent }: ConversationsProps) {
  const [openSession, setOpenSession] = useState<ConversationSession | null>(null);

  const query = useQuery<ConversationListResponse>({
    queryKey: ['conversations', agent.name],
    queryFn: () =>
      api<ConversationListResponse>(`/conversations?agent=${encodeURIComponent(agent.name)}`),
  });

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        <LoadingSkeleton className="h-10 w-full" />
        <LoadingSkeleton className="h-10 w-full" />
        <LoadingSkeleton className="h-10 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <EmptyState
        title="Could not load conversations"
        description="The backend returned an error."
      />
    );
  }

  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No conversations yet"
        description="Once the agent handles messages, sessions will show up here."
      />
    );
  }

  return (
    <>
      <div className="rounded-md border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-2 px-4 font-semibold">Session</th>
              <th className="py-2 px-4 font-semibold">First message</th>
              <th className="py-2 px-4 font-semibold">Last activity</th>
              <th className="py-2 px-4 font-semibold text-right">Messages</th>
              <th className="py-2 px-4 font-semibold text-right">Size</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr
                key={s.session_id}
                className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
                onClick={() => setOpenSession(s)}
              >
                <td className="py-3 px-4 font-mono text-xs">{s.session_id.slice(0, 12)}…</td>
                <td className="py-3 px-4 text-sm text-muted-foreground max-w-md truncate">
                  {s.first_message_preview ?? <span className="italic">(empty)</span>}
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  {s.last_activity_at ? formatRelative(s.last_activity_at) : '—'}
                </td>
                <td className="py-3 px-4 text-right text-sm">{s.message_count}</td>
                <td className="py-3 px-4 text-right text-xs text-muted-foreground">
                  {formatBytes(s.size_bytes)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={!!openSession}
        onOpenChange={(open) => !open && setOpenSession(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transcript viewer</DialogTitle>
            <DialogDescription>
              Full transcript rendering is coming soon. For now, you can find the
              raw .jsonl session file on disk and stream it with{' '}
              <code>jq</code>.
            </DialogDescription>
          </DialogHeader>
          {openSession && (
            <div className="text-sm space-y-1">
              <div>
                <span className="text-muted-foreground">Session ID:</span>{' '}
                <code className="font-mono text-xs">{openSession.session_id}</code>
              </div>
              <div>
                <span className="text-muted-foreground">Messages:</span>{' '}
                {openSession.message_count}
              </div>
              <div>
                <span className="text-muted-foreground">Size:</span>{' '}
                {formatBytes(openSession.size_bytes)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
